import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FunnelLegend } from './FunnelLegend';

export interface FunnelData {
  totalProducts: number;
  newProducts: number;
  updatedProducts: number;
  unchangedProducts: number;
  forcedFullRegen: boolean;
  evaluatedProducts: number;
  enrichmentSucceeded: number;
  enrichmentFailed: number;
  embeddingSucceeded: number;
  embeddingFailed: number;
  aeoPagesWritten: number;
  aeoValidated: number;
  needsReview: number;
  needsResearch: number;
  aeoFailed: number;
  aeoSkipped: number;
  /** task-9l7l36x12d: products a completed sub-batch could not generate because an active category split held them (ledger status 'held'); absent on reports written before the field existed */
  aeoHeld?: number;
  /** #6024 (codex-reviewer + codex-reviewer-2): products deferred for a transient enrichment gate that the run never re-picked (ledger status 'enrichment_deferred') — a different unblocker from a split hold (re-enrichment, not a proposal), so never folded into aeoHeld */
  aeoEnrichmentDeferred?: number;
  reportIncompleteProductCount: number;
  reportStatusSyncFailed: boolean;
  ruleImpactIncomplete: boolean;
}

export function FunnelSection({ funnel }: { funnel: FunnelData }) {
  const evaluatedLabel = funnel.forcedFullRegen
    ? 'All products evaluated (forced regeneration)'
    : 'New + Updated evaluated';

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Pipeline Funnel</CardTitle>
        <FunnelLegend />
      </CardHeader>
      {funnel.reportIncompleteProductCount > 0 && (
        <div
          role="alert"
          className="mx-6 mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-800"
        >
          Report incomplete: {funnel.reportIncompleteProductCount.toLocaleString()}{' '}
          product{funnel.reportIncompleteProductCount === 1 ? '' : 's'} could not be recorded
          due to a database error during processing and will not appear in the drill-down below.
        </div>
      )}
      {funnel.reportStatusSyncFailed && (
        <div
          role="alert"
          className="mx-6 mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800"
        >
          Some product statuses below may be stale: the final status sync failed for this run, so
          the drill-down may still show generation-time statuses instead of final outcomes.
        </div>
      )}
      {funnel.ruleImpactIncomplete && (
        <div
          role="alert"
          className="mx-6 mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800"
        >
          Rule-impact data may be incomplete for this run: a telemetry write failed during
          processing, so the aggregates below may not reflect every comparison table.
        </div>
      )}
      <CardContent className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
        <div>
          <div className="text-muted-foreground">Total in feed</div>
          <div className="text-2xl font-semibold">{funnel.totalProducts.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">New</div>
          <div className="text-2xl font-semibold">{funnel.newProducts.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Updated</div>
          <div className="text-2xl font-semibold">{funnel.updatedProducts.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Unchanged</div>
          <div className="text-2xl font-semibold">{funnel.unchangedProducts.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">{evaluatedLabel}</div>
          <div className="text-2xl font-semibold">{funnel.evaluatedProducts.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Enriched (succ / fail)</div>
          <div className="text-2xl font-semibold">{funnel.enrichmentSucceeded.toLocaleString()} / {funnel.enrichmentFailed.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Embedded (succ / fail)</div>
          <div className="text-2xl font-semibold">{funnel.embeddingSucceeded.toLocaleString()} / {funnel.embeddingFailed.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">AEO Pages Written</div>
          <div className="text-2xl font-semibold">{funnel.aeoPagesWritten.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Validated</div>
          <div className="text-2xl font-semibold text-green-600">{funnel.aeoValidated.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Needs Review</div>
          <div className="text-2xl font-semibold text-amber-600">{funnel.needsReview.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Needs Research</div>
          <div className="text-2xl font-semibold text-purple-600">{funnel.needsResearch.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Failed / Skipped</div>
          <div className="text-2xl font-semibold text-red-600">{funnel.aeoFailed.toLocaleString()} / {funnel.aeoSkipped.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Held (category split)</div>
          <div className="text-2xl font-semibold text-sky-600">{(funnel.aeoHeld ?? 0).toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Deferred (enrichment)</div>
          <div className="text-2xl font-semibold text-sky-600">{(funnel.aeoEnrichmentDeferred ?? 0).toLocaleString()}</div>
        </div>
      </CardContent>
    </Card>
  );
}
